import { css } from "styled-system/css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Clock, Loader2, Lock, ShoppingBag, Sparkles } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { useToast } from "@/hooks/useToast";

/** Guarda el id de la BillingSubscription recién creada al redirigir a MP, para poder forzar un
 *  resync al volver del checkout (localStorage sobrevive el viaje de ida y vuelta a MP). */
const CHECKOUT_SUB_STORAGE_KEY = "sfturnos_market_checkout_sub";

type FeatureSub = {
  id: string;
  status: string | null;
  daysLeft: number | null;
  trialEndsAt: string | null;
  activeFrom: string | null;
  activeTo: string | null;
} | null;

type MarketFeature = {
  id: string;
  slug: string;
  name: string;
  category: string;
  description: string | null;
  monthlyPrice: number;
  marketStatus: "available" | "coming_soon";
  hasRegisteredInterest: boolean;
  subscription: FeatureSub;
};

function formatPrice(n: number): string {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);
}

function StatusBadge({ sub }: { sub: FeatureSub }) {
  if (!sub || !sub.status || sub.status === "EXPIRED" || sub.status === "CANCELLED") {
    return (
      <span
        className={css({
          display: "inline-flex",
          alignItems: "center",
          gap: "1",
          fontSize: "xs",
          fontWeight: "medium",
          color: "#64748b",
          bg: "#f1f5f9",
          px: "2",
          py: "0.5",
          rounded: "full",
        })}
      >
        <Lock size={10} aria-hidden />
        No activa
      </span>
    );
  }
  if (sub.status === "TRIAL") {
    return (
      <span
        className={css({
          display: "inline-flex",
          alignItems: "center",
          gap: "1",
          fontSize: "xs",
          fontWeight: "semibold",
          color: "#92400e",
          bg: "#fef3c7",
          px: "2",
          py: "0.5",
          rounded: "full",
        })}
      >
        <Clock size={10} aria-hidden />
        {sub.daysLeft != null
          ? sub.daysLeft === 0
            ? "Prueba — último día"
            : `En prueba · ${sub.daysLeft}d`
          : "En prueba"}
      </span>
    );
  }
  return (
    <span
      className={css({
        display: "inline-flex",
        alignItems: "center",
        gap: "1",
        fontSize: "xs",
        fontWeight: "semibold",
        color: "#14532d",
        bg: "#dcfce7",
        px: "2",
        py: "0.5",
        rounded: "full",
      })}
    >
      <CheckCircle size={10} aria-hidden />
      Activa
    </span>
  );
}

function ProOnlyBadge() {
  return (
    <span
      className={css({
        display: "inline-flex",
        alignItems: "center",
        gap: "1",
        fontSize: "xs",
        fontWeight: "semibold",
        color: "#92400e",
        bg: "#fef3c7",
        px: "2",
        py: "0.5",
        rounded: "full",
      })}
    >
      <Lock size={10} aria-hidden />
      Disponible en Plan Pro
    </span>
  );
}

function ComingSoonBadge() {
  return (
    <span
      className={css({
        display: "inline-flex",
        alignItems: "center",
        gap: "1",
        fontSize: "xs",
        fontWeight: "semibold",
        color: "token(colors.brand.700)",
        bg: "rgba(99,102,241,0.1)",
        px: "2",
        py: "0.5",
        rounded: "full",
      })}
    >
      <Sparkles size={10} aria-hidden />
      Próximamente
    </span>
  );
}

function FeatureCard({
  feature,
  hasProPlan,
  onNavigate,
  onUpgrade,
}: {
  feature: MarketFeature;
  hasProPlan: boolean;
  onNavigate: () => void;
  onUpgrade: () => void;
}) {
  const toast = useToast();
  const [registered, setRegistered] = useState(feature.hasRegisteredInterest);
  const [busy, setBusy] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const { refresh } = useAuth();

  const isComingSoon = feature.marketStatus === "coming_soon";
  const hasAccess =
    !isComingSoon && (feature.subscription?.status === "ACTIVE" || feature.subscription?.status === "TRIAL");
  // Un tenant Free puede tener acceso ya otorgado a mano por un admin (cortesía/trial), eso no se
  // bloquea acá — el candado solo aplica a quien todavía no tiene el módulo y quiere adquirirlo.
  const isLockedByPlan = !isComingSoon && !hasAccess && !hasProPlan;

  async function startCheckout() {
    setCheckoutBusy(true);
    const r = await fetchWithSessionSync(
      "/api/market/checkout",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ featureSlug: feature.slug }),
      },
      refresh,
    );
    if (!r.ok) {
      setCheckoutBusy(false);
      const d = (await r.json().catch(() => ({}))) as { error?: string };
      toast.error(d.error ?? "No pudimos iniciar el pago. Probá de nuevo en un momento.");
      return;
    }
    const data = (await r.json()) as { initPoint: string; subscriptionId: string };
    localStorage.setItem(CHECKOUT_SUB_STORAGE_KEY, data.subscriptionId);
    window.location.href = data.initPoint;
  }

  async function registerInterest() {
    setBusy(true);
    const r = await fetchWithSessionSync(
      "/api/market/interest",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ featureSlug: feature.slug }),
      },
      refresh,
    );
    setBusy(false);
    if (!r.ok) {
      toast.error("No pudimos registrar tu interés. Probá de nuevo en un momento.");
      return;
    }
    setRegistered(true);
    toast.success("¡Listo! Te avisamos cuando esté disponible.");
  }

  return (
    <div
      className={css({
        bg: "white",
        rounded: "xl",
        p: "5",
        borderWidth: "1px",
        borderColor: hasAccess ? "#c7d2fe" : "#e2e8f0",
        display: "flex",
        flexDir: "column",
        gap: "4",
        boxShadow: hasAccess ? "0 0 0 2px rgba(99,102,241,0.12)" : "none",
        transition: "box-shadow 200ms ease",
        _hover: { boxShadow: "0 4px 20px rgba(0,0,0,0.06)" },
      })}
    >
      <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "3" })}>
        <div>
          <p className={css({ fontSize: "xs", color: "#94a3b8", mb: "1" })}>{feature.category}</p>
          <h3 className={css({ fontSize: "md", fontWeight: "bold", color: "#0f172a", lineHeight: 1.3 })}>
            {feature.name}
          </h3>
        </div>
        {isComingSoon ? (
          <ComingSoonBadge />
        ) : isLockedByPlan ? (
          <ProOnlyBadge />
        ) : (
          <StatusBadge sub={feature.subscription} />
        )}
      </div>

      {feature.description && (
        <p className={css({ fontSize: "sm", color: "#475569", lineHeight: "1.55", flex: 1 })}>
          {feature.description}
        </p>
      )}

      <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "auto", pt: "3", borderTopWidth: "1px", borderColor: "#f1f5f9" })}>
        {isComingSoon ? (
          <span className={css({ fontSize: "xs", color: "#94a3b8" })}>Todavía no tiene precio definido.</span>
        ) : (
          <div>
            <span className={css({ fontSize: "md", fontWeight: "bold", color: "#0f172a" })}>
              {formatPrice(feature.monthlyPrice)}
            </span>
            <span className={css({ fontSize: "xs", color: "#94a3b8", ml: "1" })}>/ mes</span>
          </div>
        )}

        {isComingSoon ? (
          <button
            type="button"
            disabled={registered || busy}
            onClick={() => void registerInterest()}
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: registered ? "#64748b" : "token(colors.brand.700)",
              bg: registered ? "#f1f5f9" : "rgba(99,102,241,0.08)",
              px: "4",
              py: "2",
              rounded: "lg",
              border: "none",
              cursor: registered || busy ? "default" : "pointer",
              transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              _hover: { opacity: registered ? 1 : 0.8 },
              _active: { transform: registered ? "none" : "scale(0.97)" },
            })}
          >
            {registered ? "¡Anotado!" : busy ? "Anotando…" : "Anotarme"}
          </button>
        ) : hasAccess ? (
          <button
            type="button"
            onClick={onNavigate}
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              bg: "token(colors.brand.600)",
              px: "4",
              py: "2",
              rounded: "lg",
              border: "none",
              cursor: "pointer",
              transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              _hover: { opacity: 0.88 },
              _active: { transform: "scale(0.97)" },
            })}
          >
            Ir al módulo
          </button>
        ) : isLockedByPlan ? (
          <button
            type="button"
            onClick={onUpgrade}
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: "#92400e",
              bg: "#fef3c7",
              px: "4",
              py: "2",
              rounded: "lg",
              border: "none",
              cursor: "pointer",
              transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              _hover: { opacity: 0.85 },
              _active: { transform: "scale(0.97)" },
            })}
          >
            Actualizar plan
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void startCheckout()}
            disabled={checkoutBusy}
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: "token(colors.brand.700)",
              bg: "rgba(99,102,241,0.08)",
              px: "4",
              py: "2",
              rounded: "lg",
              border: "none",
              cursor: checkoutBusy ? "wait" : "pointer",
              transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              _hover: { opacity: 0.8 },
              _active: { transform: checkoutBusy ? "none" : "scale(0.97)" },
              _disabled: { opacity: 0.6 },
            })}
          >
            {checkoutBusy ? "Redirigiendo…" : "Solicitar acceso"}
          </button>
        )}
      </div>
    </div>
  );
}

export function DashboardMarket() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [features, setFeatures] = useState<MarketFeature[] | null>(null);
  const [hasProPlan, setHasProPlan] = useState(true);
  const [activating, setActivating] = useState(false);
  const pollingRef = useRef(false);

  const loadFeatures = useCallback(async (): Promise<MarketFeature[] | null> => {
    const r = await fetchWithSessionSync("/api/market/features", { credentials: "include" }, refresh);
    if (!r.ok) return null;
    const data = (await r.json()) as { features: MarketFeature[]; hasProPlan: boolean };
    setFeatures(data.features);
    setHasProPlan(data.hasProPlan);
    return data.features;
  }, [refresh]);

  useEffect(() => {
    void loadFeatures();
  }, [loadFeatures]);

  // Al volver del checkout hospedado de MP (?status=success&feature=slug): reconsulta cada pocos
  // segundos para reflejar la activación automáticamente sin que el usuario tenga que refrescar a
  // mano. Cada tercer intento fuerza un resync real contra MP (por si el webhook se demora o no
  // llega), igual patrón que DashboardSubscription.tsx para Pro/Enterprise.
  useEffect(() => {
    const status = searchParams.get("status");
    const targetSlug = searchParams.get("feature");
    if (status !== "success" || !targetSlug || pollingRef.current) return;
    pollingRef.current = true;
    setActivating(true);

    const subscriptionId = localStorage.getItem(CHECKOUT_SUB_STORAGE_KEY);
    let attempts = 0;
    const maxAttempts = 15;
    const RESYNC_EVERY = 3;
    let cancelled = false;

    async function tick() {
      attempts += 1;
      if (attempts % RESYNC_EVERY === 0 && subscriptionId) {
        await fetchWithSessionSync(
          `/api/billing/subscriptions/${subscriptionId}/resync`,
          { method: "POST", credentials: "include" },
          refresh,
        ).catch(() => undefined);
      }
      const latest = await loadFeatures();
      const target = latest?.find((f) => f.slug === targetSlug);
      const isActive = target?.subscription?.status === "ACTIVE" || target?.subscription?.status === "TRIAL";
      if (cancelled) return;
      if (isActive || attempts >= maxAttempts) {
        clearInterval(interval);
        setActivating(false);
        localStorage.removeItem(CHECKOUT_SUB_STORAGE_KEY);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("status");
          next.delete("feature");
          return next;
        }, { replace: true });
      }
    }

    const interval = setInterval(() => void tick(), 4000);
    void tick();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [searchParams, loadFeatures, refresh, setSearchParams]);

  function navigateToModule(feature: MarketFeature) {
    if (feature.slug === "odontogram") {
      navigate("/dashboard/odontogram");
    }
  }

  const categories = features
    ? [...new Set(features.map((f) => f.category))].sort()
    : [];

  return (
    <DashboardPageShell
      eyebrow="Market"
      title="Market"
      description="Módulos opcionales para ampliar las funcionalidades de tu agenda."
    >
      {activating && (
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            gap: "2.5",
            px: "4",
            py: "3",
            rounded: "lg",
            bg: "rgba(99,102,241,0.08)",
            borderWidth: "1px",
            borderColor: "rgba(99,102,241,0.2)",
            fontSize: "sm",
            color: "token(colors.brand.700)",
            mb: "5",
          })}
          role="status"
          aria-live="polite"
        >
          <Loader2 size={16} className={css({ color: "token(colors.brand.700)", animation: "spin 1s linear infinite", flexShrink: 0 })} aria-hidden />
          Confirmando tu pago y activando el módulo…
        </div>
      )}
      {features === null ? (
        <LoadingFrame variant="light" minHeight={240} />
      ) : features.length === 0 ? (
        <div
          className={css({
            display: "flex",
            flexDir: "column",
            alignItems: "center",
            justifyContent: "center",
            py: "20",
            gap: "3",
            color: "#94a3b8",
          })}
        >
          <ShoppingBag size={40} strokeWidth={1.25} aria-hidden />
          <p className={css({ fontSize: "sm" })}>No hay módulos disponibles por el momento.</p>
        </div>
      ) : (
        <div className={css({ display: "flex", flexDir: "column", gap: "8" })}>
          {categories.map((cat) => (
            <section key={cat}>
              <h2
                className={css({
                  fontSize: "xs",
                  fontWeight: "semibold",
                  letterSpacing: "wide",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  mb: "4",
                })}
              >
                {cat}
              </h2>
              <div
                className={css({
                  display: "grid",
                  gridTemplateColumns: { base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" },
                  gap: "4",
                })}
              >
                {features
                  .filter((f) => f.category === cat)
                  .map((f) => (
                    <FeatureCard
                      key={f.id}
                      feature={f}
                      hasProPlan={hasProPlan}
                      onNavigate={() => navigateToModule(f)}
                      onUpgrade={() => navigate("/dashboard/subscription")}
                    />
                  ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </DashboardPageShell>
  );
}
